import type { ButtonKind } from 'components/design-system/BreakoutButton'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { DialogTopSectionWithIcon } from 'components/dialogs/DialogTopSectionWithIcon'
import { useDialogs } from 'hooks/dialogs'
import { useTranslationTyped } from 'i18n/i18n'
import { useId, useState } from 'react'

export const ConfirmationDialog = ({
  title,
  titleClassName,
  subtitle,
  buttonText,
  buttonKind = 'error',
  inline = false,
  open,
  topIcon,
  testId,
  titleTestId,
  btnTestId,
  cancelBtnTestId,
  onConfirm,
  dismiss,
  secondaryButtonText,
  secondaryButtonKind = 'secondary',
  overrideSecondaryClick,
}: {
  title: string
  titleClassName?: string
  subtitle?: string | React.ReactNode
  buttonText?: string
  buttonKind?: ButtonKind
  inline?: boolean
  open?: boolean
  topIcon?: React.ReactNode
  testId?: string
  titleTestId?: string
  btnTestId?: string
  cancelBtnTestId?: string
  onConfirm: () => Promise<void>
  dismiss?: () => void
  secondaryButtonText?: string
  secondaryButtonKind?: ButtonKind
  overrideSecondaryClick?: () => Promise<void>
}) => {
  const titleId = useId()
  const [confirming, setConfirming] = useState(false)
  const [secondaryLoading, setSecondaryLoading] = useState(false)
  const { popDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const DialogToUse = inline ? InlineDialog : Dialog
  const dismissDialog = dismiss ?? popDialog

  return (
    <DialogToUse
      size="xs"
      innerClassName="flex !w-min"
      open={open}
      onDismiss={dismiss}
      ignoreBackdropClick={confirming}
      dismissOnEscapeKey
      testId={testId}
      aria-labelledby={titleId}
    >
      <DialogCloseButton
        onClick={(e) => {
          e.preventDefault()
          dismissDialog()
          return false
        }}
      />
      <div className="flex w-full flex-col justify-between text-center">
        <DialogTopSectionWithIcon
          ColoredIcon={topIcon}
          title={title}
          description={subtitle}
          titleClassName={titleClassName}
          titleId={titleId}
          titleTestId={titleTestId}
        />
        <div className="flex gap-4">
          <BreakoutButton
            disabled={confirming || secondaryLoading}
            loading={secondaryLoading}
            size="medium"
            kind={secondaryButtonKind}
            className="basis-full"
            data-testid={cancelBtnTestId}
            onClick={async () => {
              if (overrideSecondaryClick) {
                setSecondaryLoading(true)
                try {
                  await overrideSecondaryClick()
                } finally {
                  setSecondaryLoading(false)
                  dismissDialog()
                }
              } else {
                dismissDialog()
              }
            }}
          >
            {secondaryButtonText ?? tt.instructor_class.cancel()}
          </BreakoutButton>
          <BreakoutButton
            disabled={confirming || secondaryLoading}
            loading={confirming}
            size="medium"
            kind={buttonKind}
            className="basis-full"
            data-testid={btnTestId}
            onClick={() => {
              setConfirming(true)
              onConfirm().finally(() => {
                setConfirming(false)
                dismissDialog()
              })
            }}
          >
            {buttonText || tt.admin.confirm()}
          </BreakoutButton>
        </div>
      </div>
    </DialogToUse>
  )
}
